import { FloatKeyframe } from "./FloatKeyframe";
import { WeightedMode } from "./Keyframe";
export var CurveWrapMode;
(function (CurveWrapMode) {
    CurveWrapMode[CurveWrapMode["Clamp"] = 0] = "Clamp";
    CurveWrapMode[CurveWrapMode["Loop"] = 1] = "Loop";
    CurveWrapMode[CurveWrapMode["PingPong"] = 2] = "PingPong";
})(CurveWrapMode || (CurveWrapMode = {}));
export class Curve {
    constructor() {
        this.preWrapMode = CurveWrapMode.Clamp;
        this.postWrapMode = CurveWrapMode.Clamp;
        this._keys = [];
    }
    get frameData() {
        const keys = this._keys;
        const n = keys.length;
        const stride = Curve._FRAME_STRIDE;
        const data = new Array(n * stride);
        for (let i = 0; i < n; i++) {
            const k = keys[i];
            const off = i * stride;
            data[off] = k.time;
            data[off + 1] = k.value;
            data[off + 2] = k.inTangent;
            data[off + 3] = k.outTangent;
            data[off + 4] = k.inWeight;
            data[off + 5] = k.outWeight;
            data[off + 6] = k.weightedMode;
        }
        return data;
    }
    set frameData(value) {
        this._keys.length = 0;
        if (!value)
            return;
        const stride = Curve._FRAME_STRIDE;
        const count = Math.floor(value.length / stride);
        for (let i = 0; i < count; i++) {
            const off = i * stride;
            const key = new FloatKeyframe();
            key.time = value[off];
            key.value = value[off + 1];
            key.inTangent = value[off + 2];
            key.outTangent = value[off + 3];
            key.inWeight = value[off + 4];
            key.outWeight = value[off + 5];
            key.weightedMode = value[off + 6];
            this._keys.push(key);
        }
    }
    get length() {
        return this._keys.length;
    }
    get duration() {
        if (this._keys.length === 0)
            return 0;
        return this._keys[this._keys.length - 1].time - this._keys[0].time;
    }
    get startTime() {
        if (this._keys.length === 0)
            return 0;
        return this._keys[0].time;
    }
    get endTime() {
        if (this._keys.length === 0)
            return 0;
        return this._keys[this._keys.length - 1].time;
    }
    getKey(index) {
        return this._keys[index];
    }
    get keys() {
        return this._keys;
    }
    addKey(timeOrKey, value) {
        let key;
        if (typeof timeOrKey === "number") {
            key = new FloatKeyframe();
            key.time = timeOrKey;
            key.value = value;
            key.inTangent = 0;
            key.outTangent = 0;
        }
        else {
            key = timeOrKey;
        }
        let index = this._findInsertIndex(key.time);
        this._keys.splice(index, 0, key);
        return index;
    }
    removeKey(index) {
        this._keys.splice(index, 1);
    }
    clear() {
        this._keys.length = 0;
    }
    evaluate(time) {
        return this._evaluate(time);
    }
    smoothTangents() {
        const keys = this._keys;
        const count = keys.length;
        for (let i = 0; i < count; i++) {
            this.smoothTangent(i);
        }
    }
    smoothTangent(index) {
        const keys = this._keys;
        const count = keys.length;
        if (count < 2)
            return;
        const key = keys[index];
        if (index === 0) {
            const next = keys[1];
            const tangent = (next.value - key.value) / (next.time - key.time);
            key.inTangent = tangent;
            key.outTangent = tangent;
        }
        else if (index === count - 1) {
            const prev = keys[index - 1];
            const tangent = (key.value - prev.value) / (key.time - prev.time);
            key.inTangent = tangent;
            key.outTangent = tangent;
        }
        else {
            const prev = keys[index - 1];
            const next = keys[index + 1];
            const tangent = 0.5 * ((key.value - prev.value) / (key.time - prev.time) +
                (next.value - key.value) / (next.time - key.time));
            key.inTangent = tangent;
            key.outTangent = tangent;
        }
    }
    flatTangent(index) {
        const key = this._keys[index];
        if (key) {
            key.inTangent = 0;
            key.outTangent = 0;
        }
    }
    cloneTo(destObject) {
        destObject.preWrapMode = this.preWrapMode;
        destObject.postWrapMode = this.postWrapMode;
        destObject._keys.length = 0;
        for (let i = 0, n = this._keys.length; i < n; i++) {
            destObject._keys.push(this._keys[i].clone());
        }
    }
    clone() {
        const dest = new Curve();
        this.cloneTo(dest);
        return dest;
    }
    _evaluate(time) {
        const keys = this._keys;
        const count = keys.length;
        if (count === 0)
            return 0;
        if (count === 1)
            return keys[0].value;
        const firstTime = keys[0].time;
        const lastTime = keys[count - 1].time;
        if (time <= firstTime) {
            if (this.preWrapMode === CurveWrapMode.Clamp || firstTime === lastTime)
                return keys[0].value;
            time = this._wrapTime(time, firstTime, lastTime, this.preWrapMode);
        }
        else if (time >= lastTime) {
            if (this.postWrapMode === CurveWrapMode.Clamp || firstTime === lastTime)
                return keys[count - 1].value;
            time = this._wrapTime(time, firstTime, lastTime, this.postWrapMode);
        }
        const index = this._findFrameIndex(time);
        const frame = keys[index];
        const nextFrame = keys[index + 1];
        const dur = nextFrame.time - frame.time;
        if (dur <= 0)
            return frame.value;
        const t = (time - frame.time) / dur;
        return this._curveInterpolate(frame, nextFrame, t, dur);
    }
    _wrapTime(time, firstTime, lastTime, mode) {
        const duration = lastTime - firstTime;
        if (duration <= 0)
            return firstTime;
        switch (mode) {
            case CurveWrapMode.Loop: {
                let t = (time - firstTime) % duration;
                if (t < 0)
                    t += duration;
                return firstTime + t;
            }
            case CurveWrapMode.PingPong: {
                let t = (time - firstTime) % (duration * 2);
                if (t < 0)
                    t += duration * 2;
                if (t > duration)
                    t = duration * 2 - t;
                return firstTime + t;
            }
            default:
                return time;
        }
    }
    _findInsertIndex(time) {
        const keys = this._keys;
        let low = 0;
        let high = keys.length;
        while (low < high) {
            const mid = (low + high) >>> 1;
            if (keys[mid].time < time)
                low = mid + 1;
            else
                high = mid;
        }
        return low;
    }
    _findFrameIndex(time) {
        const keys = this._keys;
        let low = 0;
        let high = keys.length - 2;
        while (low <= high) {
            const mid = (low + high) >>> 1;
            if (keys[mid + 1].time <= time)
                low = mid + 1;
            else if (keys[mid].time > time)
                high = mid - 1;
            else
                return mid;
        }
        return Math.max(0, Math.min(low, keys.length - 2));
    }
    _isStandardHermite(weightMode, nextWeightMode) {
        return ((weightMode & WeightedMode.Out) === 0) && ((nextWeightMode & WeightedMode.In) === 0);
    }
    _hermiteInterpolate(frame, nextFrame, t, dur) {
        const t0 = frame.outTangent;
        const t1 = nextFrame.inTangent;
        if (Number.isFinite(t0) && Number.isFinite(t1)) {
            const t2 = t * t;
            const t3 = t2 * t;
            const a = 2.0 * t3 - 3.0 * t2 + 1.0;
            const b = t3 - 2.0 * t2 + t;
            const c = t3 - t2;
            const d = -2.0 * t3 + 3.0 * t2;
            return a * frame.value + b * t0 * dur + c * t1 * dur + d * nextFrame.value;
        }
        return frame.value;
    }
    _hermiteWeightedInterpolate(frameValue, frameTime, frameOutWeight, frameOutTangent, nextFrameValue, nextFrameTime, nextFrameInWeight, nextFrameInTangent, time) {
        const Eps = 2.22e-16;
        const x = time;
        const x1 = frameTime;
        const y1 = frameValue;
        const wt1 = frameOutWeight;
        const x2 = nextFrameTime;
        const y2 = nextFrameValue;
        const wt2 = nextFrameInWeight;
        const dx = x2 - x1;
        let dy = y2 - y1;
        dy = Math.max(Math.abs(dy), Eps) * (dy < 0 ? -1 : 1);
        let yp1 = frameOutTangent;
        let yp2 = nextFrameInTangent;
        if (!Number.isFinite(yp1) || !Number.isFinite(yp2)) {
            return frameValue;
        }
        yp1 = yp1 * dx / dy;
        yp2 = yp2 * dx / dy;
        const wt2s = 1 - wt2;
        let t = 0.5;
        let t2 = 0;
        if (Math.abs(wt1 - 0.33333334) < 0.0001 && Math.abs(wt2 - 0.33333334) < 0.0001) {
            t = x;
            t2 = 1 - t;
        }
        else {
            while (true) {
                t2 = (1 - t);
                const fg = 3 * t2 * t2 * t * wt1 + 3 * t2 * t * t * wt2s + t * t * t - x;
                if (Math.abs(fg) <= 2.5 * Eps)
                    break;
                const fpg = 3 * t2 * t2 * wt1 + 6 * t2 * t * (wt2s - wt1) + 3 * t * t * (1 - wt2s);
                const fppg = 6 * t2 * (wt2s - 2 * wt1) + 6 * t * (1 - 2 * wt2s + wt1);
                const fpppg = 18 * wt1 - 18 * wt2s + 6;
                t -= (6 * fg * fpg * fpg - 3 * fg * fg * fppg) / (6 * fpg * fpg * fpg - 6 * fg * fpg * fppg + fg * fg * fpppg);
            }
        }
        const y = 3 * t2 * t2 * t * wt1 * yp1 + 3 * t2 * t * t * (1 - wt2 * yp2) + t * t * t;
        return y * dy + y1;
    }
    _curveInterpolate(frame, nextFrame, t, dur) {
        if (!frame.weightedMode || this._isStandardHermite(frame.weightedMode, nextFrame.weightedMode)) {
            return this._hermiteInterpolate(frame, nextFrame, t, dur);
        }
        else {
            return this._hermiteWeightedInterpolate(frame.value, frame.time, frame.outWeight, frame.outTangent, nextFrame.value, nextFrame.time, nextFrame.inWeight, nextFrame.inTangent, t);
        }
    }
}
Curve._FRAME_STRIDE = 7;
